'use client';

import UserDropDown from '@/components/common/UserDropDown';
import { Separator } from '@/components/ui/separator';
import useNotify from '@/hooks/useNotify';
import { openWindow } from '@/redux/slice/windowSlice';
import { MenuItem, SubmenuItem } from '@/utils/types/header';
import { WinDataType } from '@/utils/types/window';
import Image from 'next/image';
import Link from 'next/link';
import { memo, MouseEvent, SetStateAction, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import useMenuFormPermissions from '../permissions/useMenuFormPermissions';
import Notification from './Notification';
import OpenWindowsMenu from './OpenWindowsMenu';

// Only used by OPTION A (mega-menu) below — rename back to MEGA_MENU_COLUMN_ORDER if that option goes active again.
const _MEGA_MENU_COLUMN_ORDER = ['Equipment', 'Finance', 'Operations', 'CCC', 'Sales', 'IT'];
const groupSubmenuByCategory = (submenu: SubmenuItem[]) => {
  const groups: {
    category?: string;
    items: SubmenuItem[];
  }[] = [];
  submenu.forEach(subItem => {
    const lastGroup = groups[groups.length - 1];
    if (lastGroup && lastGroup.category === subItem.category) {
      lastGroup.items.push(subItem);
    } else {
      groups.push({
        category: subItem.category,
        items: [subItem]
      });
    }
  });
  return groups;
};
const Header = () => {
  const dispatch = useDispatch();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const [submenuIndex, setSubmenuIndex] = useState<null | number>(null);
  const [isNavbarOpen, setIsNavbarOpen] = useState(false);

  // OPTION B (accordion) / OPTION C (flyout) shared state.
  const [openCategory, setOpenCategory] = useState<string | null>(null);
  const {
    menuItems,
    isLoading
  } = useMenuFormPermissions();
  const {
    triggerNotify,
    NotifyComponent
  } = useNotify();
  const navbarRef = useRef<HTMLDivElement | null>(null);
  const handleNavbarToggle = () => {
    setIsNavbarOpen(prevState => !prevState);
  };
  const handleOpenCreditForm = () => {
    const modelTitle = 'Pending Credit, Price & Bad Debt Write Off and Order Verification';
    dispatch(openWindow({
      title: modelTitle,
      componentKey: 'credit',
      id: null,
      winData: {
        tabName: 'buyoutQuote'
      }
    }));
  };
  const handleMenuClick = (title: string, componentKey: string, size?: {
    width: number;
    height: number;
  }, isAdjustable?: boolean, winData?: WinDataType) => {
    if (isNavbarOpen) {
      handleNavbarToggle();
    }
    if (componentKey === 'rentalConversion') {
      triggerNotify({
        notifyType: 'none',
        notifyMessage: 'The old Rental Conversion form has been retired. Please use the Buyout Quote Approval system.',
        onYes: () => {
          handleOpenCreditForm();
        },
        onClose: () => {
          handleOpenCreditForm();
        }
      });
      return;
    }
    dispatch(openWindow({
      title,
      componentKey,
      id: null,
      size,
      isAdjustable: isAdjustable,
      winData
    }));
  };
  const toggleDropdown = (index: number) => {
    if (submenuIndex === index && isDropdownOpen) {
      setIsDropdownOpen(false);
      setSubmenuIndex(null);
    } else {
      setIsDropdownOpen(true);
      setSubmenuIndex(index);
    }
  };
  const closeAll = () => {
    setIsNavbarOpen(false);
    setIsDropdownOpen(false);
    setSubmenuIndex(null);
    setIsNotificationOpen(false);
  };
  const handleClickOutside = (event: MouseEvent) => {
    if (navbarRef.current && !navbarRef.current.contains(event.target as Node)) {
      closeAll();
    }
  };
  useEffect(() => {
    const handleDocumentClick = (event: MouseEvent) => handleClickOutside(event);
    document.addEventListener('mousedown', handleDocumentClick as unknown as EventListener);
    return () => {
      document.removeEventListener('mousedown', handleDocumentClick as unknown as EventListener);
    };
  }, []);
  const closeDropdown = () => {
    setIsDropdownOpen(false);
    setSubmenuIndex(null);
  };
  function handleItemClick(index: SetStateAction<number | null>) {
    setIsDropdownOpen(!isDropdownOpen || submenuIndex !== index);
    setSubmenuIndex(index);
  }
  const handleItemMouseOver = (e: React.MouseEvent | React.FocusEvent, item: MenuItem, index: number) => {
    if (item.submenu && isDropdownOpen) {
      e.preventDefault();
      toggleDropdown(index);
    } else {
      closeDropdown();
    }
  };
  const renderSubmenuButton = (submenuItem: SubmenuItem, className: string, wrap = false) => submenuItem.divider ? <Separator className="hidden md:block" /> : <button className={`w-full cursor-pointer py-1 text-start font-medium hover:bg-[#91C9F7] ${wrap ? '' : 'whitespace-nowrap'} ${className}`} onClick={() => {
    handleMenuClick(submenuItem?.windowTitle ?? submenuItem?.title ?? 'default', submenuItem?.module ?? 'default', submenuItem?.size, submenuItem?.isAdjustable, submenuItem?.winData);
    closeDropdown();
  }}>
        {submenuItem.title}
      </button>;
  return <div className="relative z-[70] flex items-center justify-between bg-[#FFFFFF2E] px-4 py-1 backdrop-blur-[50px] backdrop-filter" ref={navbarRef} data-sentry-component="Header" data-sentry-source-file="Header.tsx">
      <nav>
        <div className="flex flex-wrap items-center">
          <Link href="#" className="flex items-center space-x-3 rtl:space-x-reverse" data-sentry-element="Link" data-sentry-source-file="Header.tsx">
            <Image src="/images/MG_Logo_NextStar_White.png" width={72} height={48} alt="TRS" className="m-auto !h-[40px] w-auto" data-sentry-element="Image" data-sentry-source-file="Header.tsx" />
          </Link>
          <button data-collapse-toggle="navbar-default" type="button" className="ml-3 inline-flex h-10 w-10 items-center justify-center rounded-lg p-2 text-sm text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-200 dark:text-gray-400 dark:hover:bg-gray-700 dark:focus:ring-gray-600 xl:hidden" aria-controls="navbar-default" aria-expanded={isNavbarOpen ? 'true' : 'false'} onClick={handleNavbarToggle}>
            <span className="sr-only">Open main menu</span>
            <svg className="h-5 w-5" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 17 14" data-sentry-element="svg" data-sentry-source-file="Header.tsx">
              <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M1 1h15M1 7h15M1 13h15" data-sentry-element="path" data-sentry-source-file="Header.tsx" />
            </svg>
          </button>
          <div className={`${isNavbarOpen ? 'max-h-auto block xl:max-h-[unset]' : 'hidden max-h-0 xl:max-h-[unset]'} duration-2000 absolute left-0 top-[100%] w-full overflow-hidden pt-0 transition-[max-height,opacity] ease-in-out lg:overflow-visible xl:relative xl:left-[unset] xl:top-[unset] xl:block xl:w-auto xl:p-0`} id="navbar-default">
            <ul className="flex flex-col border border-gray-100 bg-gray-50 p-4 font-medium md:space-x-2 md:border-0 md:p-0 xl:mb-2 xl:ml-4 xl:mt-2 xl:flex-row xl:bg-transparent rtl:space-x-reverse">
              {!isLoading && menuItems.map((item, index) => <li key={item.title} className={`dropdown-menu relative ${isDropdownOpen && submenuIndex === index ? 'bg-[#FFFFFF2E]' : ''}`}>
                    <button type="button" aria-haspopup="true" aria-expanded={isDropdownOpen && submenuIndex === index ? 'true' : 'false'} className="flex w-full cursor-pointer justify-between rounded-[4px] px-2 py-2 text-base leading-[1] text-black hover:bg-gray-100 md:border-0 md:hover:bg-[#FFFFFF2E] xl:text-xs xl:text-white xl:hover:text-white 2xl:px-3" onClick={() => item.submenu ? handleItemClick(index) : handleMenuClick(item.windowTitle ?? item.title, item.module ?? 'default')} onMouseOver={e => handleItemMouseOver(e, item, index)} onFocus={e => handleItemMouseOver(e, item, index)}>
                      {item.title}
                      {item.submenu && <svg className="xl:hidden" width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M3.13523 6.15803C3.3241 5.95657 3.64052 5.94637 3.84197 6.13523L7.5 9.56464L11.158 6.13523C11.3595 5.94637 11.6759 5.95657 11.8648 6.15803C12.0536 6.35949 12.0434 6.67591 11.842 6.86477L7.84197 10.6148C7.64964 10.7951 7.35036 10.7951 7.15803 10.6148L3.15803 6.86477C2.95657 6.67591 2.94637 6.35949 3.13523 6.15803Z" fill="currentColor" fillRule="evenodd" clipRule="evenodd"></path>
                        </svg>}
                    </button>
                    {/* ============================================================
                        ADMIN FORMS DROPDOWN — 3 layout options for client review.
                        ONLY ONE must be uncommented at a time (this one is ACTIVE).
                        To switch: comment out the active block, uncomment the
                        desired OPTION B/C block below (and its matching
                        `openCategory` state near the top of this component). */}

                    {/* ===== OPTION A — separate layouts: stacked list on
                        mobile/tablet, mega-menu grid (3 cols x 2 rows) on desktop.
                        Uncomment this block + comment out OPTION B below to re-enable.
                     {item.submenu && submenuIndex === index && isDropdownOpen && (
                      <div
                        className={`w-full divide-y divide-gray-100 rounded-lg bg-white font-normal shadow md:w-auto ${
                          item.submenu.some((submenuItem) => submenuItem.category)
                            ? 'xl:fixed xl:left-1/2 xl:top-[58px] xl:w-auto xl:-translate-x-1/2'
                            : 'xl:absolute'
                        }`}
                      >
                        {item.submenu.some((submenuItem) => submenuItem.category) ? (
                          <>
                            [Small/medium screens: stacked list with inline category headers]
                            <ul className="my-2 max-h-[calc(100vh-130px)] cursor-pointer overflow-y-auto px-4 py-3 text-sm text-gray-700 transition-colors xl:hidden">
                              {item.submenu.map((submenuItem, subIndex) => {
                                const showCategoryHeader =
                                  submenuItem.category &&
                                  submenuItem.category !== item.submenu?.[subIndex - 1]?.category;
                                return (
                                  <li key={submenuItem?.title}>
                                    {showCategoryHeader && (
                                      <div className="mt-3 whitespace-nowrap px-4 pb-1 text-sm font-bold text-blue-700 first:mt-0">
                                        {submenuItem.category}
                                      </div>
                                    )}
                                    {renderSubmenuButton(submenuItem, submenuItem.category ? 'pl-16 pr-4' : 'px-4')}
                                  </li>
                                );
                              })}
                            </ul>
                             [Large screens: mega-menu, one column per department]
                            <div className="hidden max-h-[calc(100vh-160px)] cursor-pointer gap-x-8 gap-y-4 overflow-y-auto p-4 text-sm text-gray-700 transition-colors xl:grid xl:w-[662px] xl:grid-cols-3">
                              {groupSubmenuByCategory(item.submenu)
                                .sort((a, b) => MEGA_MENU_COLUMN_ORDER.indexOf(a.category ?? '') - MEGA_MENU_COLUMN_ORDER.indexOf(b.category ?? ''))
                                .map((group) => (
                                <div key={group.category ?? 'ungrouped'}>
                                  {group.category && (
                                    <div className="mb-1 whitespace-nowrap px-2 pb-1 text-sm font-bold text-blue-700">
                                      {group.category}
                                    </div>
                                  )}
                                  <ul>
                                    {group.items.map((submenuItem) => (
                                      <li key={submenuItem?.title}>
                                        {renderSubmenuButton(submenuItem, 'px-2', true)}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              ))}
                            </div>
                          </>
                        ) : (
                          <ul className="my-2 max-h-[calc(100vh-130px)] cursor-pointer overflow-y-auto px-4 py-3 text-sm text-gray-700 transition-colors">
                            {item.submenu.map((submenuItem) => (
                              <li key={submenuItem?.title}>{renderSubmenuButton(submenuItem, 'px-4')}</li>
                            ))}
                          </ul>
                        )}
                      </div>
                     )}
                     ===== END OPTION A ===== */}

                    {/* ===== OPTION B — accordion. Same layout on every
                        screen size: department headers toggle their own item list
                        open/closed, one open at a time. Uncomment this block +
                        comment out OPTION C below to re-enable.
                     {item.submenu && submenuIndex === index && isDropdownOpen && (
                      <div className="w-full divide-y divide-gray-100 rounded-lg bg-white font-normal shadow md:w-auto xl:absolute">
                        <ul className="my-2 max-h-[calc(100vh-130px)] w-72 cursor-pointer overflow-y-auto px-2 py-3 text-sm text-gray-700 transition-colors">
                          {groupSubmenuByCategory(item.submenu).map((group) => {
                            const isOpen = !group.category || openCategory === group.category;
                            return (
                              <li key={group.category ?? 'ungrouped'} className="mb-1">
                                {group.category && (
                                  <button
                                    type="button"
                                    className="flex w-full items-center justify-between rounded px-2 py-2 text-left text-sm font-bold text-blue-700 hover:bg-gray-100"
                                    onClick={() => setOpenCategory(isOpen ? null : (group.category ?? null))}
                                  >
                                    {group.category}
                                    <span className={`transition-transform ${isOpen ? 'rotate-180' : ''}`}>&#9662;</span>
                                  </button>
                                )}
                                {isOpen && (
                                  <ul className="pl-4">
                                    {group.items.map((submenuItem) => (
                                      <li key={submenuItem?.title}>{renderSubmenuButton(submenuItem, 'px-2')}</li>
                                    ))}
                                  </ul>
                                )}
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                     )}
                     ===== END OPTION B ===== */}

                    {/* ===== OPTION C (ACTIVE) — flyout. Same layout on every
                        screen size: left column lists department names, right
                        column shows the items of whichever department is
                        selected/hovered. ===== */}
                    {item.submenu && submenuIndex === index && isDropdownOpen && <div className="w-full divide-y divide-gray-100 rounded-lg bg-white font-normal shadow md:w-auto xl:absolute">
                        {item.submenu.some(submenuItem => submenuItem.category) ? <div className="flex max-h-[calc(100vh-130px)] w-[420px] text-sm text-gray-700">
                            <ul className="w-40 shrink-0 divide-y divide-gray-100 overflow-y-auto border-r border-gray-100 py-2">
                              {groupSubmenuByCategory(item.submenu).map(group => <li key={group.category}>
                                  <button type="button" className={`w-full cursor-pointer px-3 py-2 text-left text-sm font-bold hover:bg-gray-100 ${(openCategory ?? groupSubmenuByCategory(item.submenu!)[0]?.category) === group.category ? 'bg-gray-100 text-blue-700' : 'text-blue-700'}`} onMouseEnter={() => setOpenCategory(group.category ?? null)} onClick={() => setOpenCategory(group.category ?? null)}>
                                    {group.category}
                                  </button>
                                </li>)}
                            </ul>
                            <ul className="flex-1 cursor-pointer overflow-y-auto py-2">
                              {(groupSubmenuByCategory(item.submenu).find(g => g.category === openCategory)?.items ?? groupSubmenuByCategory(item.submenu)[0]?.items ?? []).map(submenuItem => <li key={submenuItem?.title}>{renderSubmenuButton(submenuItem, 'px-3')}</li>)}
                            </ul>
                          </div> : <ul className="my-2 max-h-[calc(100vh-130px)] cursor-pointer overflow-y-auto px-4 py-3 text-sm text-gray-700 transition-colors">
                            {item.submenu.map(submenuItem => <li key={submenuItem?.title}>{renderSubmenuButton(submenuItem, 'px-4')}</li>)}
                          </ul>}
                      </div>}
                  </li>)}
            </ul>
          </div>
        </div>
      </nav>
      {NotifyComponent}

      <div className="flex items-center gap-x-4">
        <OpenWindowsMenu data-sentry-element="OpenWindowsMenu" data-sentry-source-file="Header.tsx" />
        <Notification isNotificationOpen={isNotificationOpen} setIsNotificationOpen={setIsNotificationOpen} data-sentry-element="Notification" data-sentry-source-file="Header.tsx" />
        <UserDropDown data-sentry-element="UserDropDown" data-sentry-source-file="Header.tsx" />
      </div>
    </div>;
};
export default memo(Header);